import {
  BigNumber,
  isOperatorNode,
  isSymbolNode,
  MathNode,
  OperatorNode,
  SymbolNode
} from "mathjs";
import { pipe } from "ramda";

import randomFromSeed from "seneca-common/utils/functions/randomFromSeed";

import { areValuesEqual } from "./areValuesEqual";
import { BaseScope } from "./initScope";
import math from "./mathInstance";
import { allImplicit, applyMinus, removeParentheses } from "./simplifications";
import addExplicitMultiplicationAfterBracket from "./utils/addExplicitMultiplicationAfterBracket";

export { isMathNode } from "./extendMath";

export function randomNum({
  scope,
  min,
  max,
  decimals = 0
}: {
  scope: BaseScope;
  min: number;
  max: number;
  decimals?: number;
}) {
  const amplify = Math.pow(10, decimals);
  const actualMin = min * amplify;
  const actualMax = max * amplify;
  const num =
    actualMin +
    Math.floor(randomFromSeed(scope._seed) * (actualMax - actualMin + 1));

  scope._seed = Math.floor(randomFromSeed(scope._seed) * 1e9);

  return num / amplify;
}

export function polynomial({
  scope,
  maxCoefficient = 3,
  maxDegree = 1,
  positive = false
}: {
  scope: BaseScope;
  maxCoefficient?: number;
  maxDegree?: number;
  positive?: boolean;
}) {
  let coefficients;
  do {
    coefficients = Array.from(Array(maxDegree + 1)).map(
      _ =>
        randomNum({ min: 1, max: maxCoefficient, scope }) *
        (randomNum({ min: 1, max: 10, scope }) <= 2 && !positive ? -1 : 1)
    );
  } while (
    coefficients.every(it => it === 0) ||
    coefficients.reduce((acc, it) => gcd(Math.abs(it), acc), 0) !== 1
  );

  return math.rationalize(
    coefficients
      .map((coeffiecient, degree) => `${coeffiecient}*x^${degree}`)
      .join(" + ")
  );
}

export function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a;
}

export function truncate(a: number, decimals: number = 0): number {
  const power = Math.pow(10, decimals);
  return Math.trunc(a * power) / power;
}

export function subtract(node1: MathNode, node2: MathNode) {
  return new math.OperatorNode("-", "subtract", [node1, node2]);
}

export function add(node1: MathNode, node2: MathNode) {
  return new math.OperatorNode("+", "add", [node1, node2]);
}

export function pow(node1: MathNode, node2: MathNode) {
  return new math.OperatorNode("^", "pow", [node1, node2]);
}

export function sqrt(node: MathNode) {
  return new math.FunctionNode("sqrt", [node]);
}

export function divide(node1: MathNode, node2: MathNode) {
  return new math.OperatorNode("/", "divide", [node1, node2]);
}

export function multiply(node1: MathNode, node2: MathNode) {
  return new math.OperatorNode("*", "multiply", [node1, node2]);
}

function expandChildren(node: MathNode): MathNode {
  if (isOperatorNode(node)) {
    return node.map(expand);
  }

  return node;
}

export function expand(node: MathNode): MathNode {
  return pipe(
    removeParentheses,
    allImplicit,
    applyMinus,
    expandChildren,
    math.rationalize
  )(node) as any as MathNode;
}

export function getSymbols(node: MathNode): string[] {
  const symbolNodes = node.filter(isSymbolNode) as SymbolNode[];
  const symbols = symbolNodes.map(it => it.name);
  return [...new Set(symbols)];
}

export function getVariables(node: MathNode): string[] {
  const mathKeys = Object.keys(math);
  return getSymbols(node).filter(symbol => !mathKeys.includes(symbol));
}

export function isCommutative(node: MathNode): node is OperatorNode {
  return isOperatorNode(node) && ["add", "multiply"].includes(node.fn);
}

export function constant(value: number | BigNumber) {
  // We configured MathJS to use BigNumber as the default number type
  // Somehow ConstantNode only accepts number as its value
  return new math.ConstantNode(value as number);
}

export function unaryMinus(node: MathNode): MathNode {
  return new math.OperatorNode("-", "unaryMinus", [node]);
}

export function parseExpr(value: string): MathNode {
  try {
    const preprocessedValue = [
      (it: string) => it.replaceAll(" ", ""),
      addExplicitMultiplicationAfterBracket
    ].reduce((acc, fn) => fn(acc), value);

    return math.parse(preprocessedValue);
  } catch (err) {
    return new math.SymbolNode("error");
  }
}

export function isNumber(value: any): value is number {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

export function toTex(value: MathNode) {
  return allImplicit(value)
    .toTex({ implicit: "hide" })
    .replaceAll("°", "\\degree")
    .replaceAll(/\\mathrm{(.*?)}/g, "\\$1"); // We don't need \mathrm encapsulation https://katex.org/docs/supported.html#letters-and-unicode
}

export function getNodeChildren(node: MathNode): MathNode[] {
  const children: MathNode[] = [];
  node.forEach(child => children.push(child));
  return children;
}

export function equalUnordered(a: Array<any>, b: Array<any>) {
  const set = new Set();
  const equals: boolean[] = [];

  a.forEach(aValue => {
    const hasEqual = b.some((bValue, index) => {
      if (!set.has(index)) {
        if (areValuesEqual(aValue, bValue)) {
          set.add(index);
          return true;
        }
      }
      return false;
    });
    equals.push(hasEqual);
  });

  return equals;
}
